import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { appliedFilters } from './list-state.js';
import type { ListResult, ListState, ResourceClient } from './types.js';

/**
 * The hooks take only the two things they use. Accepting a whole
 * `ResourceDefinition` would drag its create/update generics through every call
 * site and make a list hook care about the shape of the edit form.
 */
export interface ResourceRef<TItem, TCreate = unknown, TUpdate = unknown> {
  key: string;
  client: ResourceClient<TItem, TCreate, TUpdate>;
}

/** Read-side refs mention only `TItem`, so any definition satisfies them. */
export interface ListResourceRef<TItem> {
  key: string;
  client: Pick<ResourceClient<TItem>, 'list'>;
}

export interface ItemResourceRef<TItem> {
  key: string;
  client: Pick<ResourceClient<TItem>, 'get'>;
}

/**
 * Query keys are derived from the resource and its state, never written by hand,
 * so invalidation after a mutation is mechanical rather than remembered
 * (docs/architecture/09-frontend.md).
 */
export const resourceKeys = {
  all: (key: string) => [key] as const,
  lists: (key: string) => [key, 'list'] as const,
  list: (key: string, state: ListState) =>
    [
      key,
      'list',
      {
        page: state.page,
        pageSize: state.pageSize,
        sort: state.sort ?? null,
        order: state.order,
        q: state.q ?? null,
        filters: appliedFilters(state),
      },
    ] as const,
  details: (key: string) => [key, 'detail'] as const,
  detail: (key: string, id: string) => [key, 'detail', id] as const,
};

export function useResourceList<TItem>(
  resource: ListResourceRef<TItem>,
  state: ListState,
  options?: { enabled?: boolean },
): UseQueryResult<ListResult<TItem>> {
  return useQuery({
    queryKey: resourceKeys.list(resource.key, state),
    queryFn: ({ signal }) => resource.client.list(state, signal),
    enabled: options?.enabled ?? true,
    // A page that is being re-fetched keeps showing the previous one; the
    // toolbar reports the refresh instead of the table blanking out.
    placeholderData: (previous) => previous,
  });
}

export function useResourceItem<TItem>(
  resource: ItemResourceRef<TItem>,
  id: string | undefined,
): UseQueryResult<TItem> {
  const get = resource.client.get;
  return useQuery({
    queryKey: resourceKeys.detail(resource.key, id ?? ''),
    queryFn: ({ signal }) => {
      if (!get) throw new Error(`Resource "${resource.key}" has no get()`);
      if (!id) throw new Error(`Resource "${resource.key}" detail requested without an id`);
      return get(id, signal);
    },
    enabled: Boolean(id && get),
  });
}

export interface ResourceMutations<TItem, TCreate, TUpdate> {
  create: UseMutationResult<TItem, unknown, TCreate>;
  update: UseMutationResult<TItem, unknown, { id: string; input: TUpdate }>;
  remove: UseMutationResult<void, unknown, string>;
}

/**
 * All three mutations are pessimistic. Optimism is opt-in per screen, because
 * for most of this domain (mandate transitions, matching runs) the server's
 * answer is not predictable — see `docs/architecture/09-frontend.md`.
 */
export function useResourceMutations<TItem, TCreate, TUpdate>(
  resource: ResourceRef<TItem, TCreate, TUpdate>,
): ResourceMutations<TItem, TCreate, TUpdate> {
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: resourceKeys.all(resource.key) });

  const create = useMutation({
    mutationFn: (input: TCreate) => {
      if (!resource.client.create) throw new Error(`Resource "${resource.key}" has no create()`);
      return resource.client.create(input);
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: TUpdate }) => {
      if (!resource.client.update) throw new Error(`Resource "${resource.key}" has no update()`);
      return resource.client.update(id, input);
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => {
      if (!resource.client.remove) throw new Error(`Resource "${resource.key}" has no remove()`);
      return resource.client.remove(id);
    },
    onSuccess: invalidate,
  });

  return { create, update, remove };
}
